import type { components } from "@/core/api/schema";
import type { BadgeTone } from "@/ui/badge";

export { formatSlots, inSlotOrder } from "@/core/display/slots";

export type CustomerPlan = components["schemas"]["BrandSubscription"];
export type CustomerPlanDetail = components["schemas"]["BrandSubscriptionDetail"];
export type PlanStatus = CustomerPlan["status"];
export type Meal = components["schemas"]["Meal"];

export const PLAN_STATUS_DISPLAY: Record<PlanStatus, { label: string; tone: BadgeTone }> = {
  SCHEDULED: { label: "Starts soon", tone: "info" },
  ACTIVE: { label: "Active", tone: "success" },
  COMPLETED: { label: "Completed", tone: "neutral" },
  CANCELLED: { label: "Cancelled", tone: "danger" },
};
export const PLAN_STATUSES: readonly PlanStatus[] = ["ACTIVE", "SCHEDULED", "COMPLETED", "CANCELLED"];

export const SOURCE_LABEL: Record<CustomerPlan["source"], string> = {
  ONLINE: "Paid online",
  OFFLINE: "Paid offline",
  ADMIN: "Free plan from Brocoly",
};

/** Every meal status in the kitchen's words, with a tone that also differs in lightness. */
export const MEAL_STATUS: Record<Meal["status"], { label: string; tone: BadgeTone }> = {
  SCHEDULED: { label: "Scheduled", tone: "neutral" },
  PAUSED: { label: "Paused", tone: "warn" },
  AUTO_PAUSED: { label: "Auto-paused", tone: "warn" },
  FORFEITED: { label: "Forfeited", tone: "neutral" },
  MISSED_BY_BRAND: { label: "Missed by your kitchen", tone: "danger" },
  DISPATCHED: { label: "On the way", tone: "info" },
  DELIVERED: { label: "Delivered", tone: "success" },
  RECEIVED: { label: "Received", tone: "success" },
  DISPUTED: { label: "Disputed", tone: "danger" },
  CANCELLED: { label: "Cancelled", tone: "neutral" },
};

export const PAUSE_KIND_LABEL: Record<NonNullable<Meal["pauseKind"]>, string> = {
  FULL_DAY: "whole day paused by the customer",
  SINGLE_MEAL: "paused by the customer",
  AUTO: "no dish chosen in time",
  HOLIDAY: "your holiday",
  MENU_CHANGED: "the dish they chose left your menu (no pause used)",
};

/** Plan meals need no tag; moved and bonus meals do. */
export const MEAL_SOURCE_TAG: Record<Meal["source"], string | null> = {
  PLAN: null,
  RESCHEDULED: "Moved",
  BONUS: "Bonus",
};

/** Plans that can still change (bonus meals). */
export const isOpen = (status: PlanStatus) => status === "SCHEDULED" || status === "ACTIVE";

/** The kitchen can report a meal it couldn't serve once the meal's cutoff has passed and it isn't delivered. */
export const canReportMissed = (meal: Meal, now = new Date()) =>
  (meal.status === "SCHEDULED" || meal.status === "DISPATCHED") && new Date(meal.cutoffAt) <= now;

/** The API's limits. */
export const PLAN_LIMITS = {
  bonusMealsPerRequest: 30,
  reasonMin: 3,
  reasonMax: 300,
  mealRangeDays: 62,
} as const;

/** For headings and lists: the name, or that the customer didn't give one. */
export const displayName = (plan: { customer: { name: string | null } }) =>
  plan.customer.name ?? "Name not given";

/** In sentences ("2 bonus meals added for …"). */
export const customerName = (plan: { customer: { name: string | null } }) =>
  plan.customer.name ?? "the customer";
